import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Modal, Badge, Alert, Spinner, ButtonGroup } from 'react-bootstrap';
import { FaPlus, FaSearch } from 'react-icons/fa';
import { validateTCNumber, validatePhoneNumber, validateIBAN, validateAge } from '../utils/validators';
import EmployeeDashboard from '../components/employee/EmployeeDashboard';
import EmployeeFilters from '../components/employee/EmployeeFilters';
import EmployeePagination from '../components/employee/EmployeePagination';
import EmployeeTable from '../components/employee/EmployeeTable';
import EmployeeCreateModal from '../components/employee/EmployeeCreateModal';
import EmployeeEditModal from '../components/employee/EmployeeEditModal';
import EmployeeDetailModal from '../components/employee/EmployeeDetailModal';
import EmployeeDeleteModal from '../components/employee/EmployeeDeleteModal';
import { Nav } from 'react-bootstrap';
import HolidayList from '../components/holiday/HolidayList';


import {
    getEmployeeStatusColor,
    getEmployeeStatusDisplayName,
    getContractTypeDisplayName,
    getDepartmentDisplayName,
    formatTurkishDate,
    formatCurrency
} from '../services/api';

// Custom hook import 
import useEmployees from '../hooks/useEmployees';

import EmployeeLeaveModal from '../components/employee/EmployeeLeaveModal';
import EmployeeLeaveListModal from '../components/employee/EmployeeLeaveListModal';

const AdminEmployeeManagement = () => {


    // **CUSTOM HOOK KULLANIMI - YENİ**
    const {
        employees,
        filteredEmployees,
        loading,
        error,
        success,
        currentPage,
        totalPages,
        searchTerm,
        filters,
        dashboardStats,
        expiringContracts,
        createNewEmployee,
        updateExistingEmployee,
        deleteExistingEmployee,
        handleFilterChange,
        handlePageChange,
        setSearchTerm,
        setSuccess,
        exportToCSV,
        clearMessages,
        loadEmployees,
        loadDashboardStats
    } = useEmployees();

    // 🔥 DEBUG: Search term and filters changes
    React.useEffect(() => {
        console.log('🔥 searchTerm changed:', searchTerm);
        console.log('🔥 filters:', filters);
    }, [searchTerm, filters]);


    // ** MODAL VE FORM STATE'LERİ**
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    // 🔥 DEBUG: selectedEmployee changes
    React.useEffect(() => {
        console.log('🔥 selectedEmployee changed:', selectedEmployee?.personalInfo?.fullName || 'NULL');
    }, [selectedEmployee]);
    const [showLeaveModal, setShowLeaveModal] = useState(false);
    const [showLeaveListModal, setShowLeaveListModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDocumentsModal, setShowDocumentsModal] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [activeTab, setActiveTab] = useState('employees');


    // **FORM DATA STATE - SADECE MODAL İÇİN**
    const [formData, setFormData] = useState({
        personalInfo: {
            fullName: '',
            tcNo: '',
            phone: '',
            email: '',
            address: '',
            ibanHolder: '',
            ibanNumber: '',
            birthDate: '',
            emergencyContact: {
                name: '',
                phone: '',
                relation: 'eş'
            },
        },
        workInfo: {
            startDate: '',
            contractType: 'belirsiz_sureli',
            contractEndDate: '',
            position: '',
            department: 'satış',
            salary: '',
            workStatus: 'aktif'
        }
    });


    // **FORM INPUT DEĞİŞİKLİKLERİNİ HANDLE ET**
    const handleFormChange = (section, field, value) => {
        if (section === 'emergencyContact') {


            // ✅ YENİ: Contract type değiştiğinde contractEndDate'i temizle
            if (section === 'workInfo' && field === 'contractType') {
                if (value === 'belirsiz_sureli') {
                    // Belirsiz süreli seçilirse bitiş tarihini temizle
                    setFormData(prev => ({
                        ...prev,
                        workInfo: {
                            ...prev.workInfo,
                            contractType: value,
                            contractEndDate: '' // Temizle
                        }
                    }));
                    return;
                }
            }

            // ✅ YENİ: Bitiş tarihi validation
            if (section === 'workInfo' && field === 'contractEndDate') {
                const startDate = formData.workInfo.startDate;
                if (startDate && value && new Date(value) <= new Date(startDate)) {
                    // Hata göster ama değeri set etme
                    console.error('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
                    return; // Değişikliği engelle
                }
            }



            setFormData(prev => ({
                ...prev,
                personalInfo: {
                    ...prev.personalInfo,
                    emergencyContact: {
                        ...prev.personalInfo.emergencyContact,
                        [field]: value
                    }
                }
            }));
        } else {

            // ✅ YENİ: Contract type değiştiğinde contractEndDate'i temizle
            if (section === 'workInfo' && field === 'contractType') {
                if (value === 'belirsiz_sureli') {
                    // Belirsiz süreli seçilirse bitiş tarihini temizle
                    setFormData(prev => ({
                        ...prev,
                        workInfo: {
                            ...prev.workInfo,
                            contractType: value,
                            contractEndDate: '' // Temizle
                        }
                    }));
                    return;
                }
            }
            // IBAN validation
            if (field === 'ibanNumber') {
                // TR ile başlamazsa ekle
                if (value && !value.startsWith('TR')) {
                    value = 'TR' + value.replace(/[^0-9]/g, '');
                }

                // IBAN uzunluk kontrolü
                if (value.length > 26) {
                    value = value.substring(0, 26);
                }

                // IBAN validation
                if (value.length >= 26 && !validateIBAN(value)) {
                    console.log('Geçersiz IBAN!');
                }
            }

            // Yaş validation
            if (field === 'birthDate') {
                if (value && !validateAge(value)) {
                    console.log('18 yaş altı personel eklenemez!');
                }
            }
            setFormData(prev => ({
                ...prev,
                [section]: {
                    ...prev[section],
                    [field]: value
                }
            }));
        }
    };

    // **DÜZENLEME MODAL'INI AÇ VE FORM'U DOLDUR**
    const handleEditClick = (employee) => {
        setSelectedEmployee(employee);
        setFormData({
            employeeId: employee._id,
            personalInfo: {
                fullName: employee.personalInfo?.fullName || '',
                tcNo: employee.personalInfo?.tcNo || '',
                phone: employee.personalInfo?.phone || '',
                email: employee.personalInfo?.email || '',
                address: employee.personalInfo?.address || '',
                birthDate: employee.personalInfo?.birthDate ?
                    new Date(employee.personalInfo.birthDate).toISOString().split('T')[0] : '',
                ibanHolder: employee.personalInfo?.ibanHolder || '',
                ibanNumber: employee.personalInfo?.ibanNumber || '',
                emergencyContact: {
                    name: employee.personalInfo?.emergencyContact?.name || '',
                    phone: employee.personalInfo?.emergencyContact?.phone || '',
                    relation: employee.personalInfo?.emergencyContact?.relation || 'eş'
                }
            },
            workInfo: {
                startDate: employee.workInfo?.startDate ?
                    new Date(employee.workInfo.startDate).toISOString().split('T')[0] : '',
                contractType: employee.workInfo?.contractType || 'belirsiz_sureli',
                contractEndDate: employee.workInfo?.contractEndDate ?
                    new Date(employee.workInfo.contractEndDate).toISOString().split('T')[0] : '',
                position: employee.workInfo?.position || '',
                department: employee.workInfo?.department || 'satış',
                salary: employee.workInfo?.salary || '',
                workStatus: employee.workInfo?.workStatus || 'aktif'
            }
        });
        setShowEditModal(true);
    };

    // **DETAY MODAL'INI AÇ**
    const handleDetailClick = (employee) => {
        setSelectedEmployee(employee);
        setShowDetailModal(true);
    };

    // **FORM'U SIFIRLA**
    const resetForm = () => {
        setFormData({
            personalInfo: {
                fullName: '',
                tcNo: '',
                phone: '',
                email: '',
                address: '',
                ibanHolder: '',
                ibanNumber: '',
                birthDate: '',
                emergencyContact: {
                    name: '',
                    phone: '',
                    relation: 'eş'
                }
            },
            workInfo: {
                startDate: '',
                contractType: 'belirsiz_sureli',
                contractEndDate: '',
                position: '',
                department: 'satış',
                salary: '',
                workStatus: 'aktif'
            }
        });
        setSelectedEmployee(null);
    };

    // **İZİN MODAL FONKSİYONLARI**
    const handleLeaveModalOpen = (employee) => {
        setSelectedEmployee(employee);
        setShowLeaveModal(true);
    };

    const handleLeaveModalClose = () => {
        setShowLeaveModal(false);
        setSelectedEmployee(null);
    };

    const handleLeaveSuccess = (message) => {
        setSuccess(message);
        loadEmployees(); // hook'tan gelen fonksiyon
        loadDashboardStats(); // hook'tan gelen fonksiyon
    };

    // **YENİ FORM HANDLER'LAR - HOOK İLE ENTEGRE**
    const handleCreateEmployee = async (e) => {
        e.preventDefault();

        const result = await createNewEmployee(formData);

        if (result.success) {
            setShowCreateModal(false);
            resetForm();
        }

        return result; // 🔥 BU SATIRI EKLE!
    };

    const handleUpdateEmployee = async (e) => {
        e.preventDefault();

        // Frontend validation
        if (formData.workInfo.contractType !== 'belirsiz_sureli' && !formData.workInfo.contractEndDate) {
            alert('Belirli süreli sözleşmelerde bitiş tarihi zorunludur');
            return;
        }

        if (formData.workInfo.contractEndDate && formData.workInfo.startDate) {
            if (new Date(formData.workInfo.contractEndDate) <= new Date(formData.workInfo.startDate)) {
                alert('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
                return;
            }
        }

        const result = await updateExistingEmployee(selectedEmployee._id, formData);

        if (result.success) {
            setShowEditModal(false);

            // Form'u temizle
            setFormData({
                personalInfo: {
                    fullName: '', tcNo: '', phone: '', email: '', address: '',
                    ibanHolder: '', ibanNumber: '', birthDate: '',
                    emergencyContact: { name: '', phone: '', relation: 'eş' }
                },
                workInfo: {
                    startDate: '', contractType: 'belirsiz_sureli', contractEndDate: '',
                    position: '', department: 'satış', salary: '', workStatus: 'aktif'
                }
            });
            // ✅ Employees listesini refresh et
            await loadEmployees();

            // ✅ Updated employee'yi backend'den tekrar fetch et
            const response = await fetch(`${process.env.REACT_APP_API_URL}/employees/${selectedEmployee._id}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const { data: updatedEmployee } = await response.json();
                setSelectedEmployee(updatedEmployee); // ✅ Updated employee set et
            }

            setShowDetailModal(true);
        }
        // Error hook'ta handle ediliyor
    };

    const handleDeleteEmployee = async (employeeId) => {
        const result = await deleteExistingEmployee(employeeId);
        // Hook kendi confirmation'ını yapıyor - şimdi şifre modal'ı yapacak
    };

    // **İZİN LİSTESİ MODAL FONKSİYONLARI**
    const handleEmployeeLeaveListOpen = (employee) => {
        setSelectedEmployee(employee);
        setShowLeaveListModal(true);
    };


    const handleEmployeeLeaveListClose = () => {
        setShowLeaveListModal(false);
        setSelectedEmployee(null);
    };

        const handleShowDeleteModal = (employee) => {
            setSelectedEmployee(employee);
            setShowDeleteModal(true);
            setDeletePassword('');
            setDeleteError('');
        };

    // **İZİN LİSTESİNDEN YENİ İZİN AÇMA**
    useEffect(() => {
        window.openLeaveModal = (employee) => {
            setSelectedEmployee(employee);
            setShowLeaveModal(true);
        };

        return () => {
            delete window.openLeaveModal;
        };
    }, []);

    // **PDF RAPORU GÖRÜNTÜLE** (senet pattern'i gibi)
    const handleEmployeePDFView = (employeeId) => {
        try {
            const token = localStorage.getItem('token');

            if (!token) {
                alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                return;
            }

            // BACKEND ENDPOINT
            const pdfUrl = `${process.env.REACT_APP_API_URL}/employees/${employeeId}/pdf?token=${token}`;

            window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        } catch (error) {
            console.error('PDF açılırken hata:', error);
            alert('PDF açılırken hata oluştu');
        }
    };


    // **LOADING DURUMUNDA SPINNER GÖSTER**
    if (loading && !employees.length) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Yükleniyor...</span>
                </Spinner>
                <p className="mt-3">Personel bilgileri yükleniyor...</p>
            </Container>
        );
    }

    return (
        <div className="admin-employee-management-page">
            <Container className="py-5">
                {/* **SAYFA BAŞLIĞI VE AKSİYON BUTONLARI** */}
                <Row className="mb-4">
                    <Col md={8}>
                        <h1>Personel Yönetimi</h1>
                        <p className="text-muted">Tüm personel bilgilerini görüntüleyin ve yönetin</p>
                    </Col>
                    <Col md={4} className="text-end">
                        <ButtonGroup>
                            <Button
                                variant="success"
                                onClick={exportToCSV}  // eskisi: handleExportCSV
                                disabled={!filteredEmployees.length}
                            >
                                📊 Excel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => setShowCreateModal(true)}
                            >
                                <FaPlus className="me-2" />
                                Yeni Personel
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>

                {/* **HATA VE BAŞARI MESAJLARI** */}
                {error && (
                    <Alert variant="danger" dismissible onClose={clearMessages}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={clearMessages}>
                        {success}
                    </Alert>
                )}

                {/* TAB NAVIGATION */}
                <Row className="mb-4">
                    <Col>
                        <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
                            <Nav.Item>
                                <Nav.Link eventKey="employees" className="fw-semibold">
                                    <i className="fas fa-users me-2"></i>Personel Listesi
                                </Nav.Link>
                            </Nav.Item>
                            <Nav.Item>
                                <Nav.Link eventKey="holidays" className="fw-semibold">
                                    <i className="fas fa-calendar me-2"></i>Resmi Tatiller
                                </Nav.Link>
                            </Nav.Item>
                        </Nav>
                    </Col>
                </Row>


                {/* EMPLOYEE TAB CONTENT */}
                {activeTab === 'employees' && (
                    <>
                {/* **DASHBOARD İSTATİSTİKLERİ** */}
                <EmployeeDashboard dashboardStats={dashboardStats} />


                {/* **ARAMA VE FİLTRELEME BÖLÜMÜ** */}
                <EmployeeFilters
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filters={filters}
                    handleFilterChange={handleFilterChange}
                    handlePageChange={handlePageChange}
                />

                {/* **PERSONEL LİSTESİ TABLOSU** */}
                <EmployeeTable
                    filteredEmployees={filteredEmployees}
                    handleDetailClick={handleDetailClick}
                    handleEditClick={handleEditClick}
                    handleLeaveModalOpen={handleLeaveModalOpen}
                    handleEmployeeLeaveListOpen={handleEmployeeLeaveListOpen}
                    handleShowDeleteModal={handleShowDeleteModal}
                />

                {/* **SAYFALAMA** */}
                <EmployeePagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                        />

                    </>
                )}

                {/* HOLIDAY TAB CONTENT */}
                {activeTab === 'holidays' && (
                    <HolidayList />
                )}
            </Container>

            {/* **YENİ PERSONEL OLUŞTURMA MODAL'I** */}
            <EmployeeCreateModal
                showCreateModal={showCreateModal}
                setShowCreateModal={setShowCreateModal}
                formData={formData}
                handleFormChange={handleFormChange}
                handleCreateEmployee={handleCreateEmployee}
                loading={loading}
            />

            {/* **PERSONEL DÜZENLEme MODAL'I** */}
            <EmployeeEditModal
                showEditModal={showEditModal}
                setShowEditModal={setShowEditModal}
                formData={formData}
                handleFormChange={handleFormChange}
                handleUpdateEmployee={handleUpdateEmployee}
                loading={loading}
            />

            {/* **PERSONEL DETAY MODAL'I** */}
            <EmployeeDetailModal
                showDetailModal={showDetailModal}
                setShowDetailModal={setShowDetailModal}
                selectedEmployee={selectedEmployee}
                handleEditClick={handleEditClick}
            />

            {/* **İZİN TALEBİ MODAL'I** */}
            <EmployeeLeaveModal
                show={showLeaveModal}
                onHide={handleLeaveModalClose}
                employee={selectedEmployee}
                onSuccess={handleLeaveSuccess}
            />

            {/* **PERSONEL SİLME MODAL'I** */}
            <EmployeeDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                selectedEmployee={selectedEmployee}
                handleDeleteEmployee={handleDeleteEmployee}
            />

            {/* **İZİN LİSTESİ MODAL'I** */}
            <EmployeeLeaveListModal
                show={showLeaveListModal}
                onHide={handleEmployeeLeaveListClose}
                employee={selectedEmployee}
            />

        </div>
    );
};

export default AdminEmployeeManagement;